import { useCallback, useEffect, useRef, useState } from "react";
import { FiClock, FiInfo } from "react-icons/fi";
import axios from "../../../api/axiosInstance";
import { readStoredUser } from "../../../utils/sessionUser";
import { hasPurchasingSettingsPermission } from "../../../utils/purchasingSettingsPermissions";

import POApprovalDialog from "../popups/POApprovalDialog";

const ENDPOINT = "/api/purchasing/automatic-requisitions/settings";

export default function AutomaticPurchaseRequisitionsSettings({ businessUnitId, languageId, onSaved }) {
  const t = (es, en, ca) => languageId === "es" ? es : languageId === "ca" ? ca : en;
  const user = readStoredUser();
  const canView = hasPurchasingSettingsPermission(user, "orders", "view");
  const canUpdate = canView && hasPurchasingSettingsPermission(user, "orders", "update");
  const [infoOpen, setInfoOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const [draft, setDraft] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const version = useRef(0);
  const mutation = useRef(false);
  const headers = { "X-Pilcore-Business-Unit-Id": String(businessUnitId) };

  const load = useCallback(async () => {
    const request = ++version.current;
    if (!canView || !businessUnitId) { setLoading(false); return; }
    setLoading(true); setError(""); setNotice(""); setSaved(false); setDraft(false);
    try {
      const response = await axios.get(ENDPOINT, { headers: { "X-Pilcore-Business-Unit-Id": String(businessUnitId) } });
      if (request !== version.current) return;
      const enabled = response.data?.automatic_purchase_requisitions_enabled === true;
      setSaved(enabled); setDraft(enabled);
    } catch (_) { if (request === version.current) setError("load"); }
    finally { if (request === version.current) setLoading(false); }
  }, [businessUnitId, canView]);

  useEffect(() => { load(); return () => { version.current += 1; }; }, [load]);

  const save = async () => {
    if (!canUpdate || mutation.current || loading || draft === saved) return;
    mutation.current = true;
    const request = version.current;
    const enabled = draft;
    setSaving(true); setError(""); setNotice("");
    try {
      await axios.put(ENDPOINT, { automatic_purchase_requisitions_enabled: enabled }, { headers });
      if (request !== version.current) return;
      setSaved(enabled); setNotice("saved"); onSaved?.(enabled);
    } catch (_) { if (request === version.current) setError("save"); }
    finally { mutation.current = false; if (request === version.current) setSaving(false); }
  };

  const title = t("Solicitudes de compra automáticas", "Automatic purchase requisitions", "Sol·licituds de compra automàtiques");
  if (!canView) return null;
  return <section className="poal-global poal-settingCard" aria-label={title}>
    <FiClock className="poal-globalIcon" aria-hidden="true" />
    <div className="poal-globalCopy">
      <div className="poal-settingHeading">
        <label className="poal-check">
          <input type="checkbox" checked={draft} disabled={!canUpdate || loading || saving || error === "load"}
            onChange={(event) => { setDraft(event.target.checked); setNotice(""); }} />
          <strong>{title}</strong>
        </label>
        <button className="poal-iconButton poal-infoButton" type="button" aria-label={title}
          aria-haspopup="dialog" onClick={() => setInfoOpen(true)}><FiInfo aria-hidden="true" /></button>
      </div>
      {loading && <small role="status">{t("Cargando ajuste…", "Loading setting…", "Carregant ajust…")}</small>}
      {notice && <small role="status">{t("Ajuste guardado.", "Setting saved.", "Ajust desat.")}</small>}
      {error && <small role="alert">{error === "load"
        ? t("No se ha podido cargar el ajuste.", "Could not load the setting.", "No s'ha pogut carregar l'ajust.")
        : t("No se ha podido guardar el ajuste.", "Could not save the setting.", "No s'ha pogut desar l'ajust.")}</small>}
    </div>
    {error === "load" ? <button type="button" className="poal-button" onClick={load}>{t("Reintentar", "Try again", "Torna-ho a provar")}</button>
      : canUpdate && <button type="button" className="poal-button" disabled={loading || saving || draft === saved} onClick={save}>
        {saving ? t("Guardando…", "Saving…", "Desant…") : t("Guardar automatización", "Save automation", "Desar automatització")}
      </button>}
    <POApprovalDialog isOpen={infoOpen} onClose={() => setInfoOpen(false)} title={title}>
      <p>{t(
        "Revisa cada noche el stock objetivo y las cadencias de consumo directo de esta unidad de negocio. Descuenta las compras pendientes y crea solicitudes para cubrir lo que falta. El horario se configura en Cron.",
        "Checks this business unit's par stock and direct-consumption cadences each night. Deducts pending purchases and creates requisitions for uncovered needs. The run time is configured in Cron.",
        "Revisa cada nit l'estoc objectiu i les cadències de consum directe d'aquesta unitat de negoci. Descompta les compres pendents i crea sol·licituds per cobrir el que falta. L'horari es configura a Cron.")}</p>
      <p>{t("Las solicitudes quedan pendientes de revisión y conversión a pedido.", "Requisitions remain pending review and conversion to purchase orders.", "Les sol·licituds queden pendents de revisió i conversió a comanda.")}</p>
    </POApprovalDialog>
  </section>;
}
